/**
 * Calendar Utilities
 * Functions for generating ICS files and Google Calendar URLs from reminders.
 */

export interface CalendarEvent {
  id: string
  title: string
  description?: string | null
  dueDate: Date | string
  reminderType?: string
}

/**
 * Format a date to ICS format (YYYYMMDDTHHMMSSZ)
 */
function formatDateToICS(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return (
    date.getUTCFullYear().toString() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    'T' +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    'Z'
  )
}

/**
 * Format a date for Google Calendar URL (YYYYMMDDTHHMMSSZ)
 */
function formatDateForGoogle(date: Date): string {
  return formatDateToICS(date)
}

/**
 * Escape special characters for ICS format
 */
function escapeICS(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
}

/**
 * Convert reminder to a Date object with a default time of 9 AM local
 */
function getEventDate(dueDate: Date | string): Date {
  const date = typeof dueDate === 'string' ? new Date(dueDate) : dueDate
  // Set to 9 AM local time for the event
  date.setHours(9, 0, 0, 0)
  return date
}

/**
 * Generate a Google Calendar URL for a single reminder
 */
export function generateGoogleCalendarUrl(event: CalendarEvent): string {
  const startDate = getEventDate(event.dueDate)
  // End date is 1 hour after start
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000)

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}`,
    details: event.description || `Lawn care reminder: ${event.reminderType || 'task'}`,
  })

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/**
 * Generate an ICS file content for multiple reminders
 */
export function generateICSContent(events: CalendarEvent[], calendarName = 'Lawn Care Reminders'): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//WhatGrassIsThis//Lawn Care Reminders//EN',
    `X-WR-CALNAME:${calendarName}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ]

  const now = new Date()
  const timestamp = formatDateToICS(now)

  for (const event of events) {
    const startDate = getEventDate(event.dueDate)
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000)

    lines.push(
      'BEGIN:VEVENT',
      `UID:${event.id}@whatgrassisthis.com`,
      `DTSTAMP:${timestamp}`,
      `DTSTART:${formatDateToICS(startDate)}`,
      `DTEND:${formatDateToICS(endDate)}`,
      `SUMMARY:${escapeICS(event.title)}`,
    )

    if (event.description) {
      lines.push(`DESCRIPTION:${escapeICS(event.description)}`)
    }

    // Add a reminder alert 1 day before
    lines.push(
      'BEGIN:VALARM',
      'TRIGGER:-P1D',
      'ACTION:DISPLAY',
      `DESCRIPTION:${escapeICS(event.title)}`,
      'END:VALARM',
    )

    lines.push('END:VEVENT')
  }

  lines.push('END:VCALENDAR')

  return lines.join('\r\n')
}

/**
 * Trigger download of an ICS file in the browser
 */
export function downloadICSFile(events: CalendarEvent[], filename = 'lawn-care-reminders.ics'): void {
  const content = generateICSContent(events)
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
